import React, { useState, useEffect } from 'react';
import { X, Loader2, UserCog } from 'lucide-react';

interface EditUserModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (userData: {
        firstName: string;
        lastName: string;
        role: string;
    }) => Promise<void>;
    isUpdating: boolean;
    user: {
        id: string;
        firstName: string;
        lastName: string;
        email: string;
        role: string;
    } | null;
    currentUserId: string;
}

const EditUserModal: React.FC<EditUserModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    isUpdating,
    user,
    currentUserId
}) => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        role: 'user',
    });

    const [errors, setErrors] = useState<{
        firstName?: string;
        lastName?: string;
    }>({});

    const [originalRole, setOriginalRole] = useState<string>('user');

    // Pre-populate form when user changes
    useEffect(() => {
        if (user) {
            setFormData({
                firstName: user.firstName,
                lastName: user.lastName,
                role: user.role,
            });
            setOriginalRole(user.role);
        }
    }, [user]);

    const formatRoleName = (role: string): string => {
        const roleNames: { [key: string]: string } = {
            'super_admin': 'Super Admin',
            'admin': 'Admin',
            'manager': 'Manager',
            'compliance': 'Compliance',
            'user': 'User'
        };
        return roleNames[role] || role;
    };

    const getRoleDescription = (role: string): string => {
        const descriptions: { [key: string]: string } = {
            'admin': 'Full access to manage users, drivers, and perform all operations',
            'manager': 'Can manage drivers and perform licence checks',
            'compliance': 'Read-only access to view drivers and check results',
            'user': 'Can perform licence checks and view assigned drivers'
        };
        return descriptions[role] || 'Standard user permissions';
    };

    const isRoleChanged = (): boolean => {
        return formData.role !== originalRole;
    };

    const validateForm = (): boolean => {
        const newErrors: typeof errors = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        await onSubmit(formData);

        // Reset form after successful submission
        setFormData({
            firstName: '',
            lastName: '',
            role: 'user',
        });
        setErrors({});
    };

    const handleClose = () => {
        // Reset form when closing
        setFormData({
            firstName: '',
            lastName: '',
            role: 'user',
        });
        setErrors({});
        onClose();
    };

    const handleInputChange = (field: keyof typeof formData, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field as keyof typeof errors]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    if (!isOpen || !user) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(105, 190, 40, 0.1)' }}>
                            <UserCog className="w-5 h-5" style={{ color: '#69BE28' }} />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Edit User</h3>
                            <p className="text-sm text-gray-500">Update user information</p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        disabled={isUpdating}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                            <input
                                type="text"
                                value={formData.firstName}
                                onChange={(e) => handleInputChange('firstName', e.target.value)}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${errors.firstName ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                                style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                disabled={isUpdating}
                            />
                            {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                            <input
                                type="text"
                                value={formData.lastName}
                                onChange={(e) => handleInputChange('lastName', e.target.value)}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${errors.lastName ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                                style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                disabled={isUpdating}
                            />
                            {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                            <input
                                type="email"
                                value={user.email}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                                disabled
                            />
                            <p className="mt-1 text-xs text-gray-500">Email cannot be changed due to unique constraint</p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                            <select
                                value={formData.role}
                                onChange={(e) => handleInputChange('role', e.target.value)}
                                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                    user?.id === currentUserId ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
                                }`}
                                style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                disabled={isUpdating || user?.id === currentUserId}
                            >
                                <option value="user">User</option>
                                <option value="compliance">Compliance</option>
                                <option value="manager">Manager</option>
                                <option value="admin">Admin</option>
                            </select>
                            {user?.id === currentUserId && (
                                <p className="mt-1 text-xs text-gray-500">You cannot change your own role</p>
                            )}
                        </div>
                    </div>

                    {/* Info Box */}
                    {isRoleChanged() ? (
                        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-sm font-semibold text-amber-900 mb-2">Warning: Role change detected</p>
                            <div className="text-sm text-amber-800 space-y-2">
                                <div className="flex items-start">
                                    <span className="font-medium mr-2">Current:</span>
                                    <span>{formatRoleName(originalRole)}</span>
                                </div>
                                <div className="flex items-start">
                                    <span className="font-medium mr-2">New:</span>
                                    <span>{formatRoleName(formData.role)}</span>
                                </div>
                                <div className="mt-2 pt-2 border-t border-amber-200">
                                    <p className="font-medium mb-1">New role permissions:</p>
                                    <p className="text-xs">{getRoleDescription(formData.role)}</p>
                                </div>
                                <p className="mt-2 text-xs font-medium">Changes take effect immediately. The user may need to log out and back in.</p>
                            </div>
                        </div>
                    ) : (
                        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                            <p className="text-sm text-gray-600">
                                <strong>Note:</strong> Changes will take effect immediately. The user may need to log out and back in to see updated permissions.
                            </p>
                        </div>
                    )}

                    {/* Footer */}
                    <div className="flex justify-end space-x-3 pt-6 mt-6 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={isUpdating}
                            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isUpdating}
                            className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center ${
                                isRoleChanged()
                                    ? 'bg-amber-500 hover:bg-amber-600'
                                    : 'hover:opacity-90'
                            }`}
                            style={!isRoleChanged() ? { backgroundColor: '#69BE28' } : {}}
                        >
                            {isUpdating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {isUpdating ? 'Updating...' : isRoleChanged() ? 'Confirm Role Change' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditUserModal;